import { forwardRef } from "react";

import { Button, type ButtonProps } from "antd";

import { IconCircleNotch } from "@btnet/blueprint-icons";
import { cn } from "@btnet/utilities-core";

import "./BuiButton.less";

// buiButtonTypes are the available button types as a constant array
export const buiButtonTypes = [
  "primary",
  "secondary",
  "tertiary",
  "danger",
  "brandprimary",
  "brandsecondary",
  "secondarydanger",
  "link",
] as const;

// TBuiButtonTypes is the type for button types based on the constant array
export type TBuiButtonTypes = (typeof buiButtonTypes)[number];

/**
 * IBuiButtonProps is the interface for BuiButton props.
 * Extends the antd ButtonProps, omitting and narrowing `type`.
 */
export interface IBuiButtonProps extends Omit<ButtonProps, "type"> {
  /** The required data-testid so it can be granular for testing. */
  testid: string;
  /** Recognizable analyticId used for analytics tagging. */
  analyticId?: string;
  /** Only intended for use with tertiary buttons to apply styling for a selected state. */
  selected?: boolean;
  type?: TBuiButtonTypes;
}

/**
 * BuiButton component - a styled wrapper around antd Button.
 * Supports various types, loading state, and custom classes.
 * Forwards its ref to the underlying button element (e.g. for dropdown focus return).
 * @param props - IBuiButtonProps defining the button's behavior and appearance
 * @returns JSX.Element representing the button
 * @example
 * <BuiButton type="primary" testid="button-test" icon={<IconCircle />}>
 *   Click me
 * </BuiButton>
 */
export const BuiButton = forwardRef<HTMLElement, IBuiButtonProps>(
  (props, ref) => {
    const {
      children,
      icon,
      testid,
      id,
      className,
      type = "primary",
      loading,
      selected,
      ...rest
    } = props;

    return (
      <Button
        {...rest}
        ref={ref}
        id={id}
        data-testid={testid}
        className={cn(
          "bui-button",
          `bui-button-${type}`,
          {
            "bui-button-loading": loading,
            "bui-button-icon-only": !children && icon,
            "bui-button-selected": selected,
          },
          className,
        )}
        aria-label={loading ? "Loading" : rest["aria-label"]}
      >
        {loading && (
          <div className="bui-button-icon-loading">
            <IconCircleNotch />
          </div>
        )}
        <>
          {icon}
          {children}
        </>
      </Button>
    );
  },
);

BuiButton.displayName = "BuiButton";
